import React from 'react';
import DOMPurify from 'dompurify';
import { Headphones, Calendar, User } from 'lucide-react';
import type { RmContentAudio } from '../../types/rmContent';
import { buildVimeoEmbedUrl, buildSimplecastEmbedUrl } from '../../utils/urlUtils';
import RmContentFooter from './RmContentFooter';

interface RmAudioViewerProps {
  content: RmContentAudio;
}

const formatDate = (dateStr: string): string => {
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(dateStr));
  } catch {
    return '';
  }
};

const RmAudioViewer: React.FC<RmAudioViewerProps> = ({ content }) => {
  const teacherNames = content.teacher
    ?.map((t: any) => (typeof t === 'string' ? t : t.title || t.slug || ''))
    .filter(Boolean);

  return (
    <div>
      {/* Episode hero card */}
      <div className="bg-gradient-to-br from-rm-primary-700 to-rm-primary-700/90 px-6 py-6 sm:py-8">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-start gap-5">
          {content.image?.src ? (
            <img
              src={content.image.src}
              alt={content.title || ''}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl object-cover shadow-lg flex-shrink-0"
              loading="lazy"
            />
          ) : (
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Headphones className="w-12 h-12 text-white/40" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white leading-tight">
              {content.title}
            </h1>
            {content.subtitle && (
              <p className="mt-2 text-sm sm:text-base text-white/80 leading-relaxed">{content.subtitle}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60 mt-3">
              {content.publishedAt && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(content.publishedAt)}
                </span>
              )}
              {teacherNames?.length > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  {teacherNames.join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
        {/* Vimeo video player */}
        {content.vimeoId && (
          <div className="w-full bg-gray-950 rounded-xl overflow-hidden shadow-lg aspect-video">
            <iframe
              src={buildVimeoEmbedUrl(content.vimeoId)}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              referrerPolicy="strict-origin-when-cross-origin"
              title={content.title || 'Audio'}
              loading="lazy"
            />
          </div>
        )}

        {/* Podcast player */}
        {content.podcastProviderId && (
          <div className="bg-rm-bg-50 rounded-xl p-4 shadow-soft">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Listen to this episode</p>
            <iframe
              src={buildSimplecastEmbedUrl(content.podcastProviderId)}
              width="100%"
              height="200"
              frameBorder="0"
              scrolling="no"
              allow="autoplay; encrypted-media"
              title={content.title || 'Podcast Player'}
              className="block w-full rounded-lg"
              style={{ minHeight: 200 }}
              loading="lazy"
            />
          </div>
        )}

        {/* Show notes */}
        {content.description && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Headphones className="w-4 h-4 text-rm-accent-500" />
              <h2 className="text-base font-semibold text-rm-ink-900">Show Notes</h2>
            </div>
            <div
              className="prose prose-base max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.description) }}
            />
          </div>
        )}

        <RmContentFooter pillar={content.pillar} teacher={content.teacher} tags={content.tags} />
      </div>
    </div>
  );
};

export default RmAudioViewer;
